/**
 * @file        src/pages/airports/AddAirport.jsx
 * @description Create / edit an airport. Field-level validation via zod
 *              (rules in ./airportRules.js, mirroring the server validator
 *              in admin_backend/src/validators/airport.validator.js).
 *              Reused for /airports/new and /airports/:id/edit.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-07-23 Claude — Support edit mode (:id) — load, prefill and PATCH the
 *                          airport, replacing the "Coming soon" stub on the edit route.
 *   - 2026-09-15 Claude — Rules moved to ./airportRules.js (mirrors the server
 *                          validator): format + character checks, not just
 *                          min/max. Codes upper-case as you type, errors clear
 *                          on edit and validate on blur, and the server's
 *                          duplicate-code 409 lands on the Airport Code field.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import { get, post, patch } from '../../api/client';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { airportSchema, fieldError, CODE_FIELDS } from './airportRules';

const empty = { code: '', name: '', city: '', citycode: '', country: '', countrycode: '' };

export default function AddAirport() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [form, setForm] = useState(empty);
  const [errors, setErrors] = useState({});

  const { data: existing, isLoading, isError } = useQuery({
    queryKey: qk.airports.one(id),
    queryFn: () => get(`/airports/${id}`),
    enabled: isEdit,
  });

  useEffect(() => {
    if (existing?.data) {
      const a = existing.data;
      setForm({
        code: a.code || '',
        name: a.name || '',
        city: a.city || '',
        citycode: a.citycode || '',
        country: a.country || '',
        countrycode: a.countrycode || '',
      });
    }
  }, [existing]);

  const mut = useMutation({
    mutationFn: (body) => (isEdit ? patch(`/airports/${id}`, body) : post('/airports', body)),
    onSuccess: () => {
      toast.ok(isEdit ? 'Airport updated' : 'Airport added', `${form.code} · ${form.name} saved.`);
      qc.invalidateQueries({ queryKey: ['airports'] });
      navigate('/airports');
    },
    onError: (err) => {
      const fields = err.response?.data?.error?.fields;
      if (fields) setErrors(fields);
      toast.err('Could not save', err.response?.data?.error?.message || 'Validation failed');
    },
  });

  function onSubmit(e) {
    e.preventDefault();
    const parsed = airportSchema.safeParse(form);
    if (!parsed.success) {
      const fieldErrs = {};
      for (const i of parsed.error.issues) fieldErrs[i.path.join('.')] = i.message;
      return setErrors(fieldErrs);
    }
    setErrors({});
    mut.mutate(parsed.data);
  }

  function onChange(key) {
    return (e) => {
      const raw = e.target.value;
      const value = key in CODE_FIELDS ? raw.toUpperCase() : raw;
      setForm((f) => ({ ...f, [key]: value }));
      // A stale message under a field the user is already fixing is noise.
      setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
    };
  }

  function onBlur(key) {
    return (e) => {
      const message = fieldError(key, e.target.value);
      setErrors((prev) => ({ ...prev, [key]: message || undefined }));
    };
  }

  return (
    <>
      <PageHead
        title={isEdit ? 'Edit Airport' : 'Add Airport'}
        subtitle="Airport Management"
        actions={<button className="btn ghost" onClick={() => navigate('/airports')}><Icon name="x" /> Cancel</button>}
      />

      <article className="panel glass">
        {isEdit && isLoading ? (
          <div className="empty">Loading airport…</div>
        ) : isEdit && isError ? (
          <div className="empty">
            <p className="field-error">Could not load this airport.</p>
            <button className="btn ghost" onClick={() => navigate('/airports')}>Back to list</button>
          </div>
        ) : (
          <form className="form-grid" onSubmit={onSubmit}>
            <Field label="Airport Code" required err={errors.code}>
              <input value={form.code} onChange={onChange('code')} onBlur={onBlur('code')}
                     maxLength={CODE_FIELDS.code} autoCapitalize="characters" spellCheck={false}
                     placeholder="e.g. DEL" />
            </Field>
            <Field label="Airport Name" required err={errors.name}>
              <input value={form.name} onChange={onChange('name')} onBlur={onBlur('name')}
                     maxLength={150} placeholder="e.g. Indira Gandhi International" />
            </Field>
            <Field label="City" required err={errors.city}>
              <input value={form.city} onChange={onChange('city')} onBlur={onBlur('city')}
                     maxLength={100} placeholder="e.g. New Delhi" />
            </Field>
            <Field label="City Code" required err={errors.citycode}>
              <input value={form.citycode} onChange={onChange('citycode')} onBlur={onBlur('citycode')}
                     maxLength={CODE_FIELDS.citycode} autoCapitalize="characters" spellCheck={false}
                     placeholder="DEL" />
            </Field>
            <Field label="Country" required err={errors.country}>
              <input value={form.country} onChange={onChange('country')} onBlur={onBlur('country')}
                     maxLength={100} placeholder="India" />
            </Field>
            <Field label="Country Code" required err={errors.countrycode}>
              <input value={form.countrycode} onChange={onChange('countrycode')} onBlur={onBlur('countrycode')}
                     maxLength={CODE_FIELDS.countrycode} autoCapitalize="characters" spellCheck={false}
                     placeholder="IN" />
            </Field>

            <div className="form-actions">
              {!isEdit && (
                <button type="button" className="btn ghost" onClick={() => { setForm(empty); setErrors({}); }}>Reset</button>
              )}
              <button className="btn primary" type="submit" disabled={mut.isPending}>
                <Icon name="plus" /> {mut.isPending ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Airport'}
              </button>
            </div>
          </form>
        )}
      </article>
    </>
  );
}

function Field({ label, required, err, children }) {
  return (
    <div className="field">
      <label>{label} {required ? <span className="req">*</span> : null}</label>
      {children}
      {err ? <div className="field-error">{err}</div> : null}
    </div>
  );
}
